import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useConnectionState, useAuth } from '../lib/hooks';
import { onlineStore } from '../lib/onlineStore';
import { db } from '../lib/db';
import { processOutbox } from '../lib/offline';
import { ShopMenu } from './ShopMenu';
import { notificationsApi } from '../lib/domain';
import { onUnreadChanged } from '../lib/unread';

// PWA-008: Online-/Offline- und Sync-Status müssen jederzeit sichtbar sein,
// nicht nur als vorübergehendes Banner. Nutzeranfrage: die Anzeige
// unterscheidet jetzt zwischen echtem Offline, automatisch erkannter
// schwacher Verbindung (Timeout) und manuell erzwungenem Offline-Modus, und
// ist per Klick umschaltbar, ohne die tatsächliche Internetverbindung zu trennen.
function StatusIndicator({ browserOnline, forcedOffline, degraded, pendingCount }: { browserOnline: boolean; forcedOffline: boolean; degraded: boolean; pendingCount: number }) {
  let label = 'Online';
  let color = '#1d5c3e';
  if (forcedOffline) {
    label = 'Manuell offline';
    color = 'var(--color-danger)';
  } else if (!browserOnline) {
    label = 'Offline';
    color = 'var(--color-danger)';
  } else if (degraded) {
    label = 'Schwache Verbindung';
    color = 'var(--color-warning)';
  } else if (pendingCount > 0) {
    label = `Synchronisiert (${pendingCount})`;
    color = '#4d8dff';
  }
  return (
    <button
      type="button"
      className="row"
      style={{ gap: 4, fontSize: '0.8rem', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
      onClick={() => onlineStore.setForcedOffline(!forcedOffline)}
      role="status"
      aria-label={`Verbindungsstatus: ${label}. Tippen zum ${forcedOffline ? 'Beenden des manuellen Offline-Modus' : 'manuellen Umschalten auf offline'}.`}
      title={forcedOffline ? 'Tippen, um den manuellen Offline-Modus zu beenden' : 'Tippen, um manuell auf offline umzuschalten (z. B. bei schlechtem Empfang)'}
    >
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} aria-hidden="true" />
      {label}
    </button>
  );
}

// Glocke mit Zähler ungelesener Benachrichtigungen (Aktualisierung bei Navigation, Fokus und alle 60 s).
function NotificationBell() {
  const [unread, setUnread] = useState(0);
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    const refresh = () =>
      notificationsApi
        .list()
        .then((r) => !cancelled && setUnread(r.unreadCount))
        .catch(() => undefined);
    void refresh();
    const interval = setInterval(refresh, 60_000);
    const onVisible = () => document.visibilityState === 'visible' && void refresh();
    document.addEventListener('visibilitychange', onVisible);
    const off = onUnreadChanged(refresh);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      off();
    };
  }, [location.pathname]);

  return (
    <Link to="/notifications" aria-label={unread > 0 ? `Benachrichtigungen (${unread} ungelesen)` : 'Benachrichtigungen'} title="Benachrichtigungen" style={{ position: 'relative', textDecoration: 'none' }}>
      🔔
      {unread > 0 && (
        <span className="badge danger" style={{ position: 'absolute', top: -8, right: -10, fontSize: '0.65rem', padding: '0 5px' }}>
          {unread > 9 ? '9+' : unread}
        </span>
      )}
    </Link>
  );
}

export function Layout() {
  const { online, browserOnline, forcedOffline, degraded } = useConnectionState();
  const { user } = useAuth();
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function refresh() {
      const count = await db.outbox.count();
      if (!cancelled) setPendingCount(count);
    }
    refresh();
    const interval = setInterval(refresh, 4000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [online]);

  return (
    <div className="app-shell">
      <a href="#main-content" className="skip-link">
        Zum Inhalt springen
      </a>
      {!online && (
        <div className="offline-banner">
          {forcedOffline
            ? 'Manuell offline - '
            : degraded
              ? 'Schwache Verbindung erkannt - '
              : 'Offline - '}
          Änderungen werden lokal gespeichert und später synchronisiert.
          {forcedOffline && (
            <button style={{ marginLeft: 8 }} onClick={() => onlineStore.setForcedOffline(false)}>
              Wieder online gehen
            </button>
          )}
        </div>
      )}
      {online && pendingCount > 0 && (
        <div className="offline-banner" style={{ background: '#4d8dff' }}>
          {pendingCount} Änderung(en) werden synchronisiert…
          <button style={{ marginLeft: 8 }} onClick={() => void processOutbox()}>
            Jetzt
          </button>
        </div>
      )}
      <header className="topbar">
        <strong>Einkaufslisten</strong>
        {user && <ShopMenu />}
        <div className="row" style={{ gap: 8 }}>
          <StatusIndicator browserOnline={browserOnline} forcedOffline={forcedOffline} degraded={degraded} pendingCount={pendingCount} />
          {user && <NotificationBell />}
          {user && <span className="muted">{user.username ?? user.email}</span>}
        </div>
      </header>
      <main className="app-main" id="main-content">
        <Outlet />
      </main>
      <footer className="app-footer">
        <Link to="/impressum">Impressum</Link>
        <Link to="/datenschutz">Datenschutz</Link>
      </footer>
      {user && (
        <nav className="bottom-nav" aria-label="Hauptnavigation">
          <NavLink to="/lists" className={({ isActive }) => (isActive ? 'active' : '')}>
            📋<span>Listen</span>
          </NavLink>
          <NavLink to="/products" className={({ isActive }) => (isActive ? 'active' : '')}>
            🛒<span>Produkte</span>
          </NavLink>
          <NavLink to="/compare" className={({ isActive }) => (isActive ? 'active' : '')}>
            💶<span>Preise</span>
          </NavLink>
          <NavLink to="/groups" className={({ isActive }) => (isActive ? 'active' : '')}>
            👥<span>Gruppen</span>
          </NavLink>
          <NavLink to="/settings" className={({ isActive }) => (isActive ? 'active' : '')}>
            ⚙️<span>Einstellungen</span>
          </NavLink>
        </nav>
      )}
    </div>
  );
}
